import { getActiveNodes } from "@/components/expression/utils/getActiveNodes";
import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { isSupSubNode } from "@/models/utils/semanticChecks";

import { OperatorSymbolNodeModel } from "./OperatorSymbolNodeModel";
import { UnitNodeModel } from "./UnitNodeModel";

import type { DocumentModel } from "../DocumentModel";
import type { SymbolNodeModel } from "./SymbolNodeModel";
import type { NodePreview } from "@/components/expression/preview/types";
import type { SuggestionItem } from "@/components/suggestions/types";
import type { NodeModel } from "@/models/nodes/types";
import type { LabelNodePosition, UnitNodePlacement } from "@corca/data";

export function isFunctionOperatorParameterChild(node?: NodeModel, index?: number): boolean {
  if (!node) {
    return false;
  }

  return (
    (node.is(OperatorNodeModel) && node.placement === "function" && index === 1) ||
    !!node.ancestors.find(
      (n) =>
        n.is(OperatorNodeModel) &&
        n.placement === "function" &&
        (n.parameters[1]?.id === node.id ||
          n.parameters[1]?.descendants.find((childNode) => childNode.id === node.id)),
    ) ||
    (node.is(LabelNodeModel) && index === 0) ||
    !!node.ancestors.find(
      (n) =>
        n.is(LabelNodeModel) &&
        (n.inlineNode?.id === node.id ||
          n.inlineNode?.descendants.find((childNode) => childNode.id === node.id)),
    )
  );
}

export function isFunctionParameterChild(node?: NodeModel) {
  if (!node) {
    return false;
  }

  const isFuncParameterChild = !!node.ancestors.find((n) => n.is(FunctionNodeModel));
  const isFuncOperatorParameterChild = isFunctionOperatorParameterChild(node);

  return isFuncOperatorParameterChild || isFuncParameterChild;
}

function isRootIndex(node?: NodeModel) {
  return (
    node?.parent?.is(OperatorNodeModel) &&
    node.parent.placement === "function" &&
    node.parent.renderView === "root"
  );
}

export function getRootIndex(node?: NodeModel) {
  return isRootIndex(node) ? node : node?.ancestors.find(isRootIndex);
}

export function isLabelChild(node?: NodeModel) {
  if (!node) {
    return false;
  }

  return !!node.ancestors.find((n) => n.is(LabelNodeModel));
}

export function isUnitChild(node?: NodeModel) {
  if (!node) {
    return false;
  }

  return !!node.ancestors.find((n) => n.is(UnitNodeModel));
}

export type UnitContainingNode = Extract<NodeModel, { unitNode: UnitNodeModel | undefined }>;
export type SymbolContainingNode = Extract<NodeModel, { symbolNode: SymbolNodeModel | undefined }>;

export function hasUnit(
  node?: NodeModel,
  placement?: UnitNodePlacement,
): node is UnitContainingNode & { unitNode: UnitNodeModel } {
  if (!node || !("unitNode" in node)) {
    return false;
  }

  if (!placement) {
    return !!node.unitNode;
  }

  return node.unitNode?.placement === placement;
}

export function hasNodeChildSymbolNode(node?: NodeModel): node is Maybe<SymbolContainingNode> {
  let symbolNode = node;

  if (isSupSubNode(symbolNode)) {
    const [baseNode] = symbolNode.prevSiblings;
    symbolNode = baseNode;
  }

  return (
    !!symbolNode &&
    (symbolNode.is(VariableNodeModel) ||
      symbolNode.is(ConstantNodeModel) ||
      symbolNode.is(FunctionNodeModel) ||
      symbolNode.is(OperatorSymbolNodeModel))
  );
}

export function getSymbolContainingNode(node?: NodeModel): SymbolContainingNode | null {
  if (isSupSubNode(node)) {
    const [baseNode] = node.prevSiblings;
    return hasNodeChildSymbolNode(baseNode) ? baseNode : null;
  }

  return hasNodeChildSymbolNode(node) ? node : null;
}

export function canSetColor(node: NodeModel): node is VariableNodeModel | ConstantNodeModel {
  return node.is(VariableNodeModel) || node.is(ConstantNodeModel);
}

export const KEY_SEPARATOR = ":↹:";

export function keyToSymbol(key: string) {
  return key.split(KEY_SEPARATOR)[2] ?? "";
}

type UniqueKeyOptions = {
  labelTexts?: { position: LabelNodePosition; text: string }[];
  ignoreLabels?: boolean;
  functionParams?: boolean;
};

/**
 * @deprecated Prefer using `NodeLookupModel` instead for better performance and consistency.
 */
export function uniqNodeNameKey<
  T extends VariableNodeModel | ConstantNodeModel | FunctionNodeModel,
>(node: T | NodePreview[T["type"]] | SuggestionItem, options?: UniqueKeyOptions): string {
  const { labelTexts, ignoreLabels, functionParams } = options ?? {};
  const name = "symbol" in node ? node.symbol : node.value;
  let text = `${KEY_SEPARATOR}${node.type}${KEY_SEPARATOR}${name ?? ""}${KEY_SEPARATOR}`;

  if (!("symbolNode" in node)) {
    const labelStr =
      labelTexts && !ignoreLabels
        ? labelTexts.map((l) => `${l.position}:${l.text}`).join("")
        : undefined;
    return labelStr ? `${text}${KEY_SEPARATOR}${labelStr}` : `${text}`;
  }

  const decorators = (node.symbolNode?.decorators || []).map((d) => d.name).join(";");

  text = `${text}${decorators}`;

  if (ignoreLabels) {
    return text;
  }

  const labels = node.symbolNode?.nonEmptyAttachments
    .map((att) => {
      return `${att.position}:${att.textContent.trim()}`;
    })
    .join(";");

  if (labels) {
    text = `${text}${KEY_SEPARATOR}${labels}`;
  }

  if (functionParams && node.type === "variable" && node.hasParams) {
    text = `${text}${KEY_SEPARATOR}${node.parameters.length}`;
  }

  return text;
}

export function isNodeHighlighted(doc: DocumentModel, node: NodeModel | undefined): boolean {
  const { hasSelectedNodes } = doc.selection;
  const { editingNode } = doc.input;

  if (!node || editingNode || hasSelectedNodes) {
    return false;
  }

  const [activeNode] = getActiveNodes(doc.cursorContext());
  return activeNode === node;
}
